import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Power } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eyebrow, DisplayHeading } from "@/components/editorial";
import {
  createFreeCode,
  listFreeCodes,
  setFreeCodeActive,
} from "@/lib/free-codes.functions";

export const Route = createFileRoute("/admin/codes")({
  head: () => ({
    meta: [
      { title: "Free review codes — Tiger's Eye Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminCodesPage,
});

export default function AdminCodesPage() {
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [maxUses, setMaxUses] = useState("1");
  const [customCode, setCustomCode] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "free-review-codes"],
    queryFn: () => listFreeCodes(),
  });

  const createMutation = useMutation({
    mutationFn: (input: { note?: string; maxUses?: number; code?: string }) =>
      createFreeCode({ data: input }),
    onSuccess: (result) => {
      toast.success(`Code ${result.code} created`);
      setNote("");
      setMaxUses("1");
      setCustomCode("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "free-review-codes"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleMutation = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      setFreeCodeActive({ data: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "free-review-codes"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const copyCode = (code: string) => {
    void navigator.clipboard.writeText(code).then(
      () => toast.success(`Copied ${code}`),
      () => toast.error("Couldn't copy — select the code manually."),
    );
  };

  return (
    <div>
      <Eyebrow className="mb-2">Marketing</Eyebrow>
      <DisplayHeading level={2} className="mb-2">Free review codes</DisplayHeading>
      <p className="text-sm text-ink-secondary mb-8 max-w-2xl">
        Generate codes for YouTubers, student ambassadors and giveaways. Students
        redeem them at <span className="font-mono">/redeem</span>. Each code has a
        use limit and can be switched off at any time.
      </p>

      <form
        className="mb-10 grid gap-4 rounded-xl border border-rule bg-surface p-5 sm:grid-cols-[1fr_120px_1fr_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          createMutation.mutate({
            note: note || undefined,
            maxUses: Number(maxUses) || 1,
            code: customCode || undefined,
          });
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="code-note">Note (who it&apos;s for)</Label>
          <Input
            id="code-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Andy Stapleton video"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="code-uses">Max uses</Label>
          <Input
            id="code-uses"
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="code-custom">Custom code (optional)</Label>
          <Input
            id="code-custom"
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value)}
            placeholder="Leave blank to generate"
          />
        </div>
        <Button type="submit" disabled={createMutation.isPending}>
          <Plus className="mr-1.5 h-4 w-4" />
          {createMutation.isPending ? "Creating…" : "Create code"}
        </Button>
      </form>

      {isLoading ? (
        <p className="text-sm text-ink-tertiary">Loading codes…</p>
      ) : !data?.codes.length ? (
        <p className="text-sm text-ink-tertiary">No codes yet — create your first one above.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-rule">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule bg-surface text-left text-xs uppercase tracking-wide text-ink-tertiary">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Note</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.codes.map((row) => (
                <tr key={row.id} className="border-b border-rule last:border-0">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => copyCode(row.code)}
                      className="inline-flex items-center gap-1.5 font-mono font-medium hover:underline"
                      title="Copy code"
                    >
                      {row.code}
                      <Copy className="h-3.5 w-3.5 text-ink-tertiary" />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{row.note ?? "—"}</td>
                  <td className="px-4 py-3 tabular-nums">
                    {row.uses_count} / {row.max_uses}
                  </td>
                  <td className="px-4 py-3 text-ink-tertiary">
                    {new Date(row.created_at).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        row.active
                          ? "rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground"
                          : "rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
                      }
                    >
                      {row.active ? "Active" : "Off"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={toggleMutation.isPending}
                      onClick={() =>
                        toggleMutation.mutate({ id: row.id, active: !row.active })
                      }
                    >
                      <Power className="mr-1 h-3.5 w-3.5" />
                      {row.active ? "Switch off" : "Switch on"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
